import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Button,
  Grid, Chip, CircularProgress, Divider, Alert,
} from '@mui/material';
import { Download, People, EventNote, AttachMoney, TrendingUp } from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import AsyncStorage from '../../services/AsyncStorage';
import dayjs from 'dayjs';

const EXPORT_TYPES = [
  {
    key: 'students',
    label: 'Students',
    icon: <People />,
    color: '#1a237e', bg: '#e8eaf6',
    desc: 'All active student records',
    endpoint: '/exportStudentsToExcel',
    columns: ['ID', 'Full Name', 'Phone', 'Course', 'Year', 'Roll No', 'Father Name', 'Mother Name', 'Academic Year', 'Status'],
    useDates: false,
  },
  {
    key: 'attendance',
    label: 'Attendance',
    icon: <EventNote />,
    color: '#2e7d32', bg: '#e8f5e9',
    desc: 'Attendance records by date range',
    endpoint: '/exportAttendanceToExcel',
    columns: ['ID', 'Student Name', 'Course', 'Year', 'Date', 'Status', 'Check In', 'Check Out'],
    useDates: true,
  },
  {
    key: 'fees',
    label: 'Fees',
    icon: <AttachMoney />,
    color: '#6a1b9a', bg: '#f3e5f5',
    desc: 'Fee payment records',
    endpoint: '/exportFeesToExcel',
    columns: ['ID', 'Student Name', 'Course', 'Year', 'Receipt No', 'Tuition Fee', 'Clinical Fee', 'Mess Fee', 'Hostel Fee', 'Status', 'Date'],
    useDates: true,
  },
  {
    key: 'leads',
    label: 'Leads',
    icon: <TrendingUp />,
    color: '#e65100', bg: '#fff3e0',
    desc: 'Student admission leads',
    endpoint: '/exportLeadsToExcel',
    columns: ['ID', 'Full Name', 'Phone', 'City', 'Country', 'Date'],
    useDates: false,
  },
];

export default function ExcelExport() {
  const [exportType, setExportType] = useState('students');
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [acYear, setAcYear] = useState('');
  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(''); 
  const [exportMode, setExportMode] = useState('all');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const init = async () => {
      const [secData, acData] = await Promise.all([
        InvokeApi(Config.main_url + apiList.getDeptartmentSection, { status: 'Active' }),
        InvokeApi(Config.main_url + apiList.getAvailableAcademicYears, {}),
      ]);
      setClasses(secData?.sections || []);
      const years = acData?.academic_years || [];
      setAcademicYears(years);
      const current = years.find(y => y.is_current) || years[0];
      if (current) setAcYear(current.value || '');
    };
    init();
  }, []);

  const activeType = EXPORT_TYPES.find(t => t.key === exportType);

  const classOptions = classes.map(c => ({
    value: `${c.standard?.trim()}||${c.section?.trim()}`,
    label: `${c.standard?.trim()} - ${c.section?.trim()}`,
  }));

  const handleExport = async () => {
    if (activeType.useDates && dayjs(startDate).isAfter(dayjs(endDate))) {
      setResult({ success: false, message: 'Start date cannot be after end date' });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const token = await AsyncStorage.getItem('token');

      const params = { academic_year: acYear };

      if (activeType.useDates) {
        params.start_date = startDate;
        params.end_date = endDate;
      }

      if (exportMode === 'class' && selectedClass) {
        const [std, sec] = selectedClass.split('||');
        params.standard = std;
        params.section = sec;
      }

      const response = await fetch(Config.main_url + activeType.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        let errMsg = `Export failed (${response.status})`;
        try {
          const errData = await response.json();
          errMsg = errData?.message || errMsg;
        } catch {}
        setResult({ success: false, message: errMsg });
        return;
      }

      const contentType = response.headers.get('Content-Type') || '';
      if (contentType.includes('application/json')) {
        const errData = await response.json();
        setResult({ success: false, message: errData?.message || 'No data found for the selected criteria' });
        return;
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition') || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);

      let filename = filenameMatch?.[1];
      if (!filename) {
        const ext = contentType.includes('csv') ? 'csv' : 'xlsx';
        filename = `${exportType}_export_${dayjs().format('YYYY-MM-DD')}.${ext}`;
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setResult({ success: true, message: `Downloaded: ${filename}` });
    } catch (err) {
      setResult({ success: false, message: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800} color="#1a237e">Data Export</Typography>
        <Typography variant="body2" color="text.secondary">Download student data </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography fontWeight={700} color="#1a237e" mb={2}>Select Export Type</Typography>

              <Grid container spacing={1.5} mb={3}>
                {EXPORT_TYPES.map(t => (
                  <Grid item xs={6} sm={3} key={t.key}>
                    <Box onClick={() => setExportType(t.key)} sx={{
                      p: 2, borderRadius: 2, cursor: 'pointer', textAlign: 'center',
                      border: '2px solid', transition: 'all 0.2s',
                      borderColor: exportType === t.key ? t.color : '#e8eaf6',
                      background: exportType === t.key ? t.bg : '#fff',
                      '&:hover': { background: t.bg, borderColor: t.color },
                    }}>
                      <Box sx={{ color: exportType === t.key ? t.color : 'text.secondary', mb: 0.5 }}>
                        {React.cloneElement(t.icon, { sx: { fontSize: 28 } })}
                      </Box>
                      <Typography fontSize={13} fontWeight={700} color={exportType === t.key ? t.color : 'text.secondary'}>
                        {t.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ mb: 2.5 }} />

              <Typography fontWeight={700} color="#1a237e" mb={2}>Filter Options</Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth size="small" label="Academic Year" value={acYear}
                    onChange={e => setAcYear(e.target.value)}>
                    {academicYears.map(y => <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>)}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth size="small" label="Scope" value={exportMode}
                    onChange={e => setExportMode(e.target.value)}>
                    <MenuItem value="all">All Classes</MenuItem>
                    <MenuItem value="class">Specific Class</MenuItem>
                  </TextField>
                </Grid>

                {exportMode === 'class' && (
                  <Grid item xs={12}>
                    <TextField select fullWidth size="small" label="Class" value={selectedClass}
                      onChange={e => setSelectedClass(e.target.value)}>
                      <MenuItem value="">— Select Class —</MenuItem>
                      {classOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </TextField>
                  </Grid>
                )}

                {activeType?.useDates && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" type="date" label="Start Date"
                        value={startDate} onChange={e => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" type="date" label="End Date"
                        value={endDate} onChange={e => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }} />
                    </Grid>
                  </>
                )}
              </Grid>

              {result && (
                <Alert severity={result.success ? 'success' : 'error'} sx={{ mt: 2, borderRadius: 2 }}>
                  {result.message}
                </Alert>
              )}

              <Button fullWidth variant="contained" size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Download />}
                onClick={handleExport} disabled={loading}
                sx={{
                  mt: 3, py: 1.5, borderRadius: 2, fontWeight: 700, fontSize: 15,
                  background: `linear-gradient(135deg,${activeType?.color || '#1a237e'},${activeType?.color || '#1565c0'}cc)`,
                  '&:hover': { opacity: 0.9 },
                }}>
                {loading ? 'Generating CSV...' : `Download ${activeType?.label} CSV`}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, background: activeType?.bg }}>
                  {activeType && React.cloneElement(activeType.icon, { sx: { color: activeType.color, fontSize: 24 } })}
                </Box>
                <Box>
                  <Typography fontWeight={800} color="#1a237e">{activeType?.label} Export</Typography>
                  <Typography fontSize={12} color="text.secondary">{activeType?.desc}</Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Typography fontSize={13} fontWeight={700} color="#1a237e" mb={1}>Columns included:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {activeType?.columns.map(c => (
                  <Chip key={c} label={c} size="small"
                    sx={{ background: activeType.bg, color: activeType.color, fontSize: 10, fontWeight: 600 }} />
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
